import { useEffect, useState, useRef } from "react";
import { InfoHint } from "../components/InfoHint";
import { RuoloBadge } from "../components/RuoloBadge";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { DatePickerField } from "../components/DatePickerField";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Landmark, Upload, Download, CheckCircle2, AlertTriangle, Trash2, Paperclip, Loader2, Plus, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import ProprietaUnitFirst from "./ProprietaUnitFirst";

export default function Proprieta() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  useEffect(() => { api.get("/proprieta/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  if (!meta) return <div className="text-lg">Caricamento...</div>;
  return user.role === "condomino" ? <CondominoView meta={meta} /> : <AdminView />;
}

/* ---------------------------------- ADMIN ---------------------------------- */
function AdminView() {
  const [condomini, setCondomini] = useState([]);
  const [filtro] = useState(() => new URLSearchParams(window.location.search).get("condominio") || "");
  useEffect(() => { api.get("/condomini").then((r) => setCondomini(r.data)).catch(() => {}); }, []);

  // Iter122fj · Export "Anagrafica Estesa" (xlsx / pdf)
  const exportEstesa = async (format) => {
    try {
      const params = { format };
      if (filtro) params.condominio_id = filtro;
      const res = await api.get("/anagrafica/export-estesa", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (format === "pdf") {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = "anagrafica_estesa.xlsx"; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(`Anagrafica estesa esportata (${format.toUpperCase()})`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossibile esportare l'anagrafica estesa");
    }
  };

  return (
    <div className="max-w-7xl" data-testid="proprieta-admin">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
          <Landmark className="w-9 h-9 text-brand" /> Anagrafica Condòmini
        </h1>
        <InfoHint section="proprieta" />
        <div className="flex flex-wrap gap-2 ml-auto">
          <Button data-testid="anagrafica-estesa-xlsx" onClick={() => exportEstesa("xlsx")}
            variant="outline" className="h-10 text-sm border-brand/40 text-brand"
            title="Esporta la tabella Anagrafica estesa in Excel (22 colonne per unità).">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Anagrafica Estesa · Excel
          </Button>
          <Button data-testid="anagrafica-estesa-pdf" onClick={() => exportEstesa("pdf")}
            variant="outline" className="h-10 text-sm border-brand/40 text-brand"
            title="Apre la tabella Anagrafica estesa in formato PDF (A4 landscape) per stampa.">
            <FileText className="w-4 h-4 mr-1.5" /> Anagrafica Estesa · PDF
          </Button>
        </div>
      </div>
      <ProprietaUnitFirst condomini={condomini} filtroInitial={filtro} />
    </div>
  );
}

/* -------------------------------- CONDOMINO -------------------------------- */
function CondominoView({ meta }) {
  const [list, setList] = useState([]);
  const load = () => api.get("/proprieta").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-3xl" data-testid="proprieta-condomino">
      <div className="flex items-center gap-2 mb-2">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
          <Landmark className="w-9 h-9 text-brand" /> I dati del mio immobile
        </h1>
        <InfoHint section="proprieta" />
      </div>
      <p className="text-lg text-slate-600 mb-4">Aggiorna i dati anagrafici e catastali del tuo immobile. Puoi modificarli in qualsiasi momento se cambiano.</p>
      {list.length === 0 ? (
        <Card className="p-5 text-center text-slate-500 text-lg border-2">Nessuna scheda immobile associata al tuo profilo. Contatta lo Studio.</Card>
      ) : (
        list.map((p) => <SchedaForm key={p.id} p={p} meta={meta} onSaved={load} />)
      )}
    </div>
  );
}

function SchedaForm({ p, meta, onSaved }) {
  const [f, setF] = useState({
    quota_proprieta: p.quota_proprieta || "", diritto_reale: p.diritto_reale || "",
    tipo_casa: p.tipo_casa || "", percentuale_detrazione: p.percentuale_detrazione || "",
    telefono: p.telefono || "", email: p.email || "", num_occupanti: p.num_occupanti || "",
    inizio_locazione: p.inizio_locazione || "", fine_locazione: p.fine_locazione || "", coinquilini: p.coinquilini || [],
    atto_notorio: !!p.atto_notorio, privacy_consenso: !!p.privacy_consenso,
  });
  const [docs, setDocs] = useState(p.documenti_locazione || []);
  const cond = isConduttore(p);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setTipoCasa = (v) => setF((s) => ({ ...s, tipo_casa: v, percentuale_detrazione: meta.detrazione_default[v] || s.percentuale_detrazione }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/proprieta/${p.id}`, f);
      toast.success("Dati salvati. Grazie!");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-4 border-2 mb-4" data-testid={`scheda-${p.id}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-bold">{p.condominio_nome}{p.unita ? ` · ${p.unita}` : ""}</h3>
        {p.completato
          ? <span className="inline-flex items-center gap-1 text-green-700 font-bold"><CheckCircle2 className="w-5 h-5" /> Completo</span>
          : <span className="inline-flex items-center gap-1 text-amber-700 font-bold"><AlertTriangle className="w-5 h-5" /> Da completare</span>}
      </div>

      {(p.indirizzo || p.codice_fiscale || p.foglio || p.ruolo) && (
        <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600 grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="anagrafica-readonly">
          {p.ruolo && <div className="flex items-center gap-2"><b>Ruolo:</b> {p.ruolo} <RuoloBadge ruolo={p.ruolo} /></div>}
          {p.codice_fiscale && <div><b>Cod. fiscale:</b> {p.codice_fiscale}</div>}
          {p.indirizzo && <div className="sm:col-span-2"><b>Indirizzo:</b> {[p.indirizzo, p.cap, p.citta, p.prov].filter(Boolean).join(" ")}</div>}
          {/* Iter122ih · Riga Catasto sempre completa: mostra ogni etichetta con "—" per i campi vuoti */}
          <div className="sm:col-span-2" data-testid="catasto-row">
            <b>Catasto:</b>{" "}
            <span>Foglio {p.foglio || "—"} · Particella {p.mappale_particella || "—"} · Subalterno {p.sub || "—"} · Sezione {p.sezione_urbana || "—"} · Comune cat. {p.comune_catastale || "—"}</span>
          </div>
          {(p.palazzina || p.gruppo_unita || p.codice_unita) && (
            <div className="sm:col-span-2"><b>Ubicazione:</b> {[p.palazzina && `Palazzina ${p.palazzina}`, p.gruppo_unita && `Gruppo ${p.gruppo_unita}`, p.codice_unita && `Cod. unità ${p.codice_unita}`].filter(Boolean).join(" · ")}</div>
          )}
          <div className="sm:col-span-2 text-xs text-slate-400 italic">Dati forniti dallo Studio. Per correzioni contatta l'amministrazione.</div>
        </div>
      )}

      {/* Iter122ih · Check totale quote di proprietà/comproprietà dell'unità */}
      {(p.numero_titolari_unita > 0 || ["Proprietario", "Comproprietario"].includes(p.ruolo)) && (() => {
        const tot = Number(p.totale_quote_proprieta_unita || 0);
        const ok = Math.abs(tot - 100) < 0.01;
        return (
          <div data-testid="quote-check-banner"
            className={`mb-4 p-3 rounded-lg border-2 flex items-start gap-2 ${ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-900"}`}>
            {ok
              ? <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
              : <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />}
            <div>
              <div className="font-bold">
                Totale quote di proprietà: <span data-testid="quote-total">{tot.toString().replace(".", ",")}%</span> ({p.numero_titolari_unita || 0} titolar{(p.numero_titolari_unita || 0) === 1 ? "e" : "i"})
              </div>
              {ok
                ? <div className="text-xs">Il totale corrisponde al 100%. Dati proprietà completi.</div>
                : <div className="text-xs"><b>Avviso:</b> il totale delle quote di proprietà non è 100%. Verifica le quote dei comproprietari e aggiorna eventualmente i valori.</div>}
            </div>
          </div>
        );
      })()}

      <div className="grid sm:grid-cols-2 gap-4">
        {cond ? (
          <>
            <Field label="Inizio locazione">
              <DatePickerField testid="f-inizio-locazione" value={f.inizio_locazione} onChange={(v) => set("inizio_locazione", v)} className="h-12" short />
            </Field>
            <Field label="Fine locazione">
              <DatePickerField testid="f-fine-locazione" value={f.fine_locazione} onChange={(v) => set("fine_locazione", v)} className="h-12" short />
            </Field>
            <Field label="N. persone che occupano stabilmente l'immobile">
              <Input data-testid="f-occupanti" type="number" min="0" value={f.num_occupanti} onChange={(e) => set("num_occupanti", e.target.value)} className="h-12" />
            </Field>
            <Field label="Telefono">
              <Input data-testid="f-telefono" value={f.telefono} onChange={(e) => set("telefono", e.target.value)} className="h-12" />
            </Field>
            <Field label="Email">
              <Input data-testid="f-email" value={f.email} onChange={(e) => set("email", e.target.value)} className="h-12" />
            </Field>
            <CoinquiliniEditor value={f.coinquilini} onChange={(v) => set("coinquilini", v)} idp="f-coinq" />
            <DocumentiLocazione pid={p.id} docs={docs} onChange={setDocs} idp="f-doc" />
          </>
        ) : (
          <>
            <Field label="% di proprietà/comproprietà">
              <Input data-testid="f-quota" value={f.quota_proprieta} onChange={(e) => set("quota_proprieta", e.target.value)} placeholder="es. 100 oppure 50" className="h-12" />
            </Field>
            <Field label="Diritto reale">
              <select data-testid="f-diritto" value={f.diritto_reale} onChange={(e) => set("diritto_reale", e.target.value)}
                className="h-12 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-base">
                <option value="">Seleziona...</option>
                {meta.diritti_reali.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Tipologia immobile">
              <select data-testid="f-tipocasa" value={f.tipo_casa} onChange={(e) => setTipoCasa(e.target.value)}
                className="h-12 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-base">
                <option value="">Seleziona...</option>
                {meta.tipi_casa.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="% detrazione fiscale">
              <Input data-testid="f-detrazione" value={f.percentuale_detrazione} onChange={(e) => set("percentuale_detrazione", e.target.value)} placeholder="auto: prima 50% / seconda 36%" className="h-12" />
            </Field>
            <Field label="Telefono">
              <Input data-testid="f-telefono" value={f.telefono} onChange={(e) => set("telefono", e.target.value)} className="h-12" />
            </Field>
            <Field label="Email">
              <Input data-testid="f-email" value={f.email} onChange={(e) => set("email", e.target.value)} className="h-12" />
            </Field>
            <Field label="N. persone che occupano stabilmente l'immobile">
              <Input data-testid="f-occupanti" type="number" min="0" value={f.num_occupanti} onChange={(e) => set("num_occupanti", e.target.value)} className="h-12" />
            </Field>
          </>
        )}
      </div>

      <div className="mt-5 p-4 rounded-lg bg-[#FBEEF3]/50 border border-[#EBC5D2]">
        <div className="text-sm font-bold uppercase tracking-wide text-brand mb-2">Dichiarazione sostitutiva di atto notorio e privacy</div>
        <p className="text-sm text-slate-700 leading-relaxed mb-3">{meta.disclosure}</p>
        <label className="flex items-start gap-2 mb-2 cursor-pointer">
          <input data-testid="f-atto" type="checkbox" checked={f.atto_notorio} onChange={(e) => set("atto_notorio", e.target.checked)} className="mt-1 w-5 h-5 accent-brand" />
          <span className="text-base">Dichiaro, sotto la mia responsabilità (atto notorio), che i dati indicati corrispondono al vero.</span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input data-testid="f-privacy" type="checkbox" checked={f.privacy_consenso} onChange={(e) => set("privacy_consenso", e.target.checked)} className="mt-1 w-5 h-5 accent-brand" />
          <span className="text-base">Acconsento al trattamento dei dati ai sensi del GDPR (Reg. UE 2016/679) per le finalità indicate.</span>
        </label>
      </div>

      <Button data-testid="save-scheda" disabled={saving || !f.atto_notorio || !f.privacy_consenso}
        className="h-12 mt-4 bg-brand hover:bg-brand-dark" onClick={save}>
        <CheckCircle2 className="w-5 h-5 mr-2" /> {saving ? "Salvataggio..." : "Salva i dati"}
      </Button>

      {/* Iter122id · Il condòmino può aggiungere comproprietari, altri diritti reali o inquilini alla propria unità */}
      <AggiungiSoggettoBlock pid={p.id} condominioNome={p.condominio_nome} meta={meta} onCreated={onSaved} />
    </Card>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-base font-bold mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

const isConduttore = (p) => (p?.ruolo || "").toLowerCase().includes("inquilino") || (p?.ruolo || "").toLowerCase().includes("conduttore");
const DOC_TIPI = [
  ["identita", "Documento d'identità"],
  ["codice_fiscale", "Codice fiscale / Tessera sanitaria"],
  ["contratto", "Contratto di locazione"],
  ["attestazione_oneri", "Attestazione assolvimento oneri condominiali"],
  ["altro", "Altro"],
];

function CoinquiliniEditor({ value, onChange, idp = "coinq" }) {
  const list = value || [];
  const add = () => onChange([...list, { nome: "", codice_fiscale: "" }]);
  const upd = (i, k, v) => onChange(list.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const rm = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="sm:col-span-2" data-testid={`${idp}-editor`}>
      <Label className="text-sm font-bold mb-1 block">Dati coinquilini</Label>
      <div className="space-y-2">
        {list.map((c, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`${idp}-row-${i}`}>
            <Input placeholder="Nome e cognome" value={c.nome || ""} onChange={(e) => upd(i, "nome", e.target.value)} className="h-11" data-testid={`${idp}-nome-${i}`} />
            <Input placeholder="Codice fiscale" value={c.codice_fiscale || ""} onChange={(e) => upd(i, "codice_fiscale", e.target.value)} className="h-11" data-testid={`${idp}-cf-${i}`} />
            <Button type="button" variant="ghost" size="icon" className="h-10 w-10 text-red-600 shrink-0" onClick={() => rm(i)} data-testid={`${idp}-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" className="mt-2 h-9" onClick={add} data-testid={`${idp}-add`}><Plus className="w-4 h-4 mr-1" /> Aggiungi coinquilino</Button>
    </div>
  );
}

function DocumentiLocazione({ pid, docs, onChange, idp = "doc" }) {
  const [uploading, setUploading] = useState(false);
  const [tipo, setTipo] = useState("identita");
  const upload = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    setUploading(true);
    try {
      let last = null;
      for (const f of arr) {
        const fd = new FormData(); fd.append("file", f);
        const r = await api.post(`/proprieta/${pid}/documento?tipo=${tipo}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        last = r.data.documenti_locazione || [];
      }
      if (last) onChange(last);
      toast.success(`${arr.length} document${arr.length === 1 ? "o caricato" : "i caricati"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setUploading(false); }
  };
  const remove = async (fid) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    try { const r = await api.delete(`/proprieta/${pid}/documento/${fid}`); onChange(r.data.documenti_locazione || []); }
    catch { toast.error("Eliminazione non riuscita"); }
  };
  return (
    <div className="sm:col-span-2 mt-1" data-testid={`${idp}-locazione`}>
      <Label className="text-sm font-bold mb-1 block flex items-center gap-1"><Paperclip className="w-4 h-4" /> Documenti (identità, codice fiscale, contratto locazione, attestazione oneri condominiali)</Label>
      <div className="flex flex-wrap items-center gap-2">
        <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="h-10 border-2 border-slate-300 rounded-lg px-2 bg-white text-sm" data-testid={`${idp}-tipo`}>
          {DOC_TIPI.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 h-10 px-3 border-2 border-dashed rounded-lg cursor-pointer text-slate-500 hover:border-[#C98AA0] text-sm" data-testid={`${idp}-upload`}>
          {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</> : <><Upload className="w-4 h-4" /> Carica documenti</>}
          <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        </label>
      </div>
      {(docs || []).length > 0 && (
        <div className="mt-2 space-y-1">
          {(docs || []).map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2 bg-slate-50" data-testid={`${idp}-item-${d.id}`}>
              <div className="min-w-0 text-sm">
                <span className="font-semibold text-slate-800 truncate">{d.filename}</span>
                <span className="text-xs text-slate-500 ml-2">{DOC_TIPI.find(([k]) => k === d.tipo)?.[1] || d.tipo}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button type="button" onClick={() => openSecureFile(`/files/${d.id}`)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand border border-brand/40 rounded px-2 py-1" data-testid={`${idp}-download-${d.id}`}><Download className="w-3.5 h-3.5" /></button>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => remove(d.id)} data-testid={`${idp}-del-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// Iter122id · Blocco "Aggiungi soggetto" nella scheda del condòmino.
// Consente di legare alla propria unità immobiliare un nuovo soggetto (comproprietario,
// altro diritto reale o inquilino). Backend: POST /api/proprieta/{pid}/soggetto.
// Iter122ie · Include tutti i campi del soggetto principale (referente): tipo casa,
// % detrazione, occupanti, telefono/telefono2, atto notorio + consenso privacy.
function AggiungiSoggettoBlock({ pid, condominioNome, meta, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ruolo, setRuolo] = useState("comproprietario");
  const [tipo, setTipo] = useState("Persona fisica");
  const [nome, setNome] = useState("");
  const [codiceFiscale, setCodiceFiscale] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [telefono2, setTelefono2] = useState("");
  const [quota, setQuota] = useState("");
  const [dirittoReale, setDirittoReale] = useState("");
  const [tipoCasa, setTipoCasa] = useState("");
  const [percDetrazione, setPercDetrazione] = useState("");
  const [numOccupanti, setNumOccupanti] = useState("");
  const [inizioLocazione, setInizioLocazione] = useState("");
  const [fineLocazione, setFineLocazione] = useState("");
  const [attoNotorio, setAttoNotorio] = useState(false);
  const [privacyConsenso, setPrivacyConsenso] = useState(false);

  const setTipoCasaAndDetrazione = (v) => {
    setTipoCasa(v);
    const def = (meta && meta.detrazione_default && meta.detrazione_default[v]) || "";
    if (def) setPercDetrazione(def);
  };

  const reset = () => {
    setRuolo("comproprietario"); setTipo("Persona fisica");
    setNome(""); setCodiceFiscale(""); setEmail(""); setTelefono(""); setTelefono2("");
    setQuota(""); setDirittoReale(""); setTipoCasa(""); setPercDetrazione("");
    setNumOccupanti(""); setInizioLocazione(""); setFineLocazione("");
    setAttoNotorio(false); setPrivacyConsenso(false);
  };

  const salva = async () => {
    if (!nome.trim()) { toast.error("Inserisci nome/denominazione"); return; }
    if (!attoNotorio || !privacyConsenso) {
      toast.error("Accetta la dichiarazione atto notorio e il consenso privacy");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/proprieta/${pid}/soggetto`, {
        ruolo, tipo, nome: nome.trim(),
        codice_fiscale: codiceFiscale.trim().toUpperCase(),
        email: email.trim().toLowerCase(),
        telefono: telefono.trim(), telefono2: telefono2.trim(),
        quota: quota.trim(), diritto_reale: dirittoReale.trim(),
        tipo_casa: tipoCasa,
        percentuale_detrazione: percDetrazione.trim(),
        num_occupanti: numOccupanti.trim(),
        inizio_locazione: inizioLocazione, fine_locazione: fineLocazione,
        atto_notorio: attoNotorio, privacy_consenso: privacyConsenso,
      });
      toast.success("Soggetto aggiunto. Lo Studio verificherà l'anagrafica.");
      reset();
      setOpen(false);
      onCreated && onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const isConduttore = ruolo === "inquilino";
  return (
    <div className="mt-4 pt-4 border-t border-slate-200">
      {!open ? (
        <Button type="button" variant="outline" onClick={() => setOpen(true)}
          data-testid={`add-soggetto-${pid}`}
          className="h-auto min-h-11 w-full sm:w-auto border-2 border-brand text-brand font-bold whitespace-normal break-words text-left py-2.5 px-3">
          <Plus className="w-5 h-5 mr-1.5 shrink-0" />
          <span className="min-w-0">
            Aggiungi soggetto
            <span className="hidden sm:inline"> (comproprietario · altro diritto · inquilino)</span>
            <span className="block sm:hidden text-xs font-normal opacity-80">comproprietario · altro diritto · inquilino</span>
          </span>
        </Button>
      ) : (
        <div className="rounded-lg border-2 border-brand/40 bg-white p-4" data-testid={`add-soggetto-form-${pid}`}>
          <div className="text-base font-bold text-brand mb-2 flex items-center gap-2">
            <Plus className="w-5 h-5" /> Nuovo soggetto per la tua unità in {condominioNome}
          </div>
          <p className="text-sm text-slate-500 mb-3">
            Aggiungi un soggetto collegato al tuo immobile. Lo Studio riceverà una notifica e verificherà l'anagrafica.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Ruolo">
              <select value={ruolo} onChange={(e) => setRuolo(e.target.value)}
                className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white"
                data-testid="new-soggetto-ruolo">
                <option value="comproprietario">Comproprietario</option>
                <option value="altro_diritto">Altro diritto reale (usufrutto, nuda proprietà, ecc.)</option>
                <option value="inquilino">Inquilino / Conduttore (locazione)</option>
              </select>
            </Field>
            <Field label="Tipo persona">
              <select value={tipo} onChange={(e) => setTipo(e.target.value)}
                className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white"
                data-testid="new-soggetto-tipo">
                <option value="Persona fisica">Persona fisica</option>
                <option value="Persona giuridica">Persona giuridica</option>
              </select>
            </Field>
            <Field label="Nome e cognome / Denominazione" full>
              <Input data-testid="new-soggetto-nome" value={nome} onChange={(e) => setNome(e.target.value)}
                className="h-11" placeholder="Es. Mario Rossi" />
            </Field>
            <Field label="Codice fiscale / P.IVA">
              <Input data-testid="new-soggetto-cf" value={codiceFiscale} onChange={(e) => setCodiceFiscale(e.target.value)}
                className="h-11" />
            </Field>
            <Field label="Email">
              <Input data-testid="new-soggetto-email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11" type="email" />
            </Field>
            <Field label="Telefono">
              <Input data-testid="new-soggetto-tel" value={telefono} onChange={(e) => setTelefono(e.target.value)}
                className="h-11" />
            </Field>
            <Field label="Telefono 2 (opz.)">
              <Input data-testid="new-soggetto-tel2" value={telefono2} onChange={(e) => setTelefono2(e.target.value)}
                className="h-11" />
            </Field>
            {!isConduttore && (
              <Field label="% di proprietà/comproprietà">
                <Input data-testid="new-soggetto-quota" value={quota} onChange={(e) => setQuota(e.target.value)}
                  className="h-11" placeholder="es. 100 oppure 50" />
              </Field>
            )}
            {!isConduttore && (
              <Field label="Diritto reale">
                <select value={dirittoReale} onChange={(e) => setDirittoReale(e.target.value)}
                  className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white"
                  data-testid="new-soggetto-diritto">
                  <option value="">Seleziona...</option>
                  {(meta.diritti_reali || []).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
            )}
            {!isConduttore && (
              <Field label="Tipologia immobile">
                <select value={tipoCasa} onChange={(e) => setTipoCasaAndDetrazione(e.target.value)}
                  className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white"
                  data-testid="new-soggetto-tipocasa">
                  <option value="">Seleziona...</option>
                  {(meta.tipi_casa || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
            )}
            {!isConduttore && (
              <Field label="% detrazione fiscale">
                <Input data-testid="new-soggetto-detrazione" value={percDetrazione}
                  onChange={(e) => setPercDetrazione(e.target.value)}
                  className="h-11" placeholder="auto: prima 50% / seconda 36%" />
              </Field>
            )}
            <Field label="N. persone che occupano stabilmente l'immobile">
              <Input data-testid="new-soggetto-occupanti" type="number" min="0" value={numOccupanti}
                onChange={(e) => setNumOccupanti(e.target.value)} className="h-11" />
            </Field>
            {isConduttore && (
              <>
                <Field label="Inizio locazione">
                  <DatePickerField testid="new-soggetto-inizio-loc" value={inizioLocazione} onChange={setInizioLocazione} className="h-11" short />
                </Field>
                <Field label="Fine locazione">
                  <DatePickerField testid="new-soggetto-fine-loc" value={fineLocazione} onChange={setFineLocazione} className="h-11" short />
                </Field>
              </>
            )}
          </div>

          <div className="mt-4 p-3 rounded-lg bg-[#FBEEF3]/50 border border-[#EBC5D2]">
            <div className="text-sm font-bold uppercase tracking-wide text-brand mb-2">Dichiarazione sostitutiva di atto notorio e privacy</div>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">{meta.disclosure}</p>
            <label className="flex items-start gap-2 mb-2 cursor-pointer">
              <input data-testid="new-soggetto-atto" type="checkbox" checked={attoNotorio}
                onChange={(e) => setAttoNotorio(e.target.checked)} className="mt-1 w-5 h-5 accent-brand" />
              <span className="text-base">Dichiaro, sotto la mia responsabilità (atto notorio), che i dati indicati per il nuovo soggetto corrispondono al vero.</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input data-testid="new-soggetto-privacy" type="checkbox" checked={privacyConsenso}
                onChange={(e) => setPrivacyConsenso(e.target.checked)} className="mt-1 w-5 h-5 accent-brand" />
              <span className="text-base">Acconsento al trattamento dei dati del nuovo soggetto ai sensi del GDPR (Reg. UE 2016/679) per le finalità indicate.</span>
            </label>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button type="button" onClick={salva} disabled={saving || !nome.trim() || !attoNotorio || !privacyConsenso}
              data-testid="new-soggetto-save"
              className="h-11 bg-brand hover:bg-brand-dark">
              <CheckCircle2 className="w-5 h-5 mr-1.5" /> {saving ? "Aggiungo…" : "Aggiungi soggetto"}
            </Button>
            <Button type="button" variant="outline" onClick={() => { reset(); setOpen(false); }}
              data-testid="new-soggetto-cancel" className="h-11">
              Annulla
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

